import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiMenu, FiX } from "react-icons/fi";

const links = [
  { to: "/", label: "Home" },
  { to: "/projects", label: "Projects" },
  { to: "/articles", label: "Articles" },
  { to: "/around-the-world", label: "Around the World" },
];

const NAMES = ["Abdelrahman", "Prof. Helal"];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [cursorVisible, setCursorVisible] = useState(true);
  const [displayText, setDisplayText] = useState("");
  const [nameIndex, setNameIndex] = useState(0);
  const [phase, setPhase] = useState("typing");

  useEffect(() => {
    const interval = setInterval(() => setCursorVisible((v) => !v), 530);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fullName = NAMES[nameIndex];

    if (phase === "typing") {
      if (displayText.length < fullName.length) {
        const t = setTimeout(() => setDisplayText(fullName.slice(0, displayText.length + 1)), 80);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => setPhase("erasing"), 1200);
        return () => clearTimeout(t);
      }
    }

    if (phase === "erasing") {
      if (displayText.length > 0) {
        const t = setTimeout(() => setDisplayText(displayText.slice(0, -1)), 50);
        return () => clearTimeout(t);
      } else {
        const t = setTimeout(() => {
          setNameIndex((i) => (i + 1) % NAMES.length);
          setPhase("typing");
        }, 300);
        return () => clearTimeout(t);
      }
    }
  }, [phase, displayText, nameIndex]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-terminal/95 backdrop-blur-sm text-brightText border-b border-terminalBorder shadow-[0_1px_20px_rgba(125,211,252,0.05)]">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-xl font-bold tracking-tight font-mono">
          {displayText}
          <span className="text-accentBlue">.</span>
          <span
            className="text-accentBlue ml-0.5"
            style={{ opacity: cursorVisible ? 1 : 0 }}
          >
            █
          </span>
        </Link>

        {/* Desktop links */}
        <div className="hidden md:flex gap-8">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`relative py-1 text-sm font-medium transition-colors hover:text-accentBlue ${
                location.pathname === link.to
                  ? "text-accentBlue"
                  : "text-dimText"
              }`}
            >
              {link.label}
              {location.pathname === link.to && (
                <motion.div
                  layoutId="nav-underline"
                  className="absolute -bottom-1 left-0 right-0 h-0.5 bg-accentBlue rounded-full shadow-[0_0_8px_rgba(125,211,252,0.5)]"
                />
              )}
            </Link>
          ))}
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden text-2xl text-dimText hover:text-accentBlue transition-colors"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <FiX /> : <FiMenu />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-terminal border-t border-terminalBorder"
          >
            <div className="px-6 pb-4 flex flex-col gap-3">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className={`text-sm font-medium transition-colors hover:text-accentBlue ${
                    location.pathname === link.to
                      ? "text-accentBlue"
                      : "text-dimText"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
